import React, { useState } from 'react';
import { MobileTimePicker } from '@mui/x-date-pickers';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import moment from 'moment';
import { startEndTimeValidator } from '../helpers';
import { mainTheme } from '../assets/theme';
import { Colors } from '../assets/theme/Colors';
import NativeMinMaxTimePicker from './Native/NativeMinMaxTimePicker';

const MinMaxTimePicker = (props) => {
  const {
    showFieldErrors = false,
    minTime,
    maxTime,
    onTimeChange,
    value,
    hasHoursForSelectedDay = false,
    weeklyHours = [],
    pickupSpecs = [],
    startDate = {},
    showWarning = false,
    inputVariant = 'outlined',
    size = 'small',
    label = '',
    InputLabelProps = {},
    fullWidth = false,
    testId = '',
    isMobileNative = false,
    ...rest
  } = props;
  const initialValue = value;
  const [errorText, setErrorText] = useState(undefined);

  const isInReceivingHours = pickupSpecs.every((spec) => {
    const receivingHours = weeklyHours.filter((time) =>
      spec.day === null ? time.day_of_week === moment(startDate.value).day() : spec.day === time.day_of_week
    );

    if (receivingHours?.length) {
      return receivingHours.find((data) =>
        moment(value, 'HH:mm:ss').isBetween(moment(data.start, 'HH:mm:ss'), moment(data.end, 'HH:mm:ss'), undefined, '[]')
      );
    }

    return false;
  });

  const clockTheme = createTheme({
    ...mainTheme,
    components: {
      MuiClockPointer: {
        styleOverrides: {
          root: {
            backgroundColor: isInReceivingHours ? Colors.green.light : Colors.red,
          },
          thumb: {
            borderColor: isInReceivingHours ? Colors.green.light : Colors.red,
            backgroundColor: isInReceivingHours ? Colors.green.light : Colors.red,
          },
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          notchedOutline: {
            borderColor: showWarning ? Colors.red : 'rgba(0, 0, 0, 0.23)',
          },
        },
      },
    },
  });

  const handleTimeChange = (momentObj) => {
    setErrorText(undefined);
    let durationDiff;
    let violationErrorList;

    if (minTime) {
      violationErrorList = startEndTimeValidator(minTime, momentObj);
      if (!violationErrorList.end.isValid && showFieldErrors) {
        setErrorText(violationErrorList.end.message);
      }

      onTimeChange({
        event: momentObj,
        isValid: violationErrorList.end.isValid,
        duration: undefined,
      });
    }

    if (maxTime) {
      violationErrorList = startEndTimeValidator(momentObj, maxTime);

      if (!violationErrorList.begin.isValid && showFieldErrors) {
        setErrorText(violationErrorList.begin.message);
      }

      durationDiff = moment.duration(moment(maxTime, 'HH:mm').diff(moment(initialValue, 'HH:mm')));
      onTimeChange({
        event: momentObj,
        isValid: violationErrorList.begin.isValid,
        duration: durationDiff,
      });
    }
  };

  if (isMobileNative) {
    return (
      <NativeMinMaxTimePicker
        value={value ? moment(value) : null}
        label={label}
        onChange={handleTimeChange}
        showFieldErrors={showFieldErrors}
        showWarning={showWarning}
        errorText={errorText}
      />
    );
  }

  return (
    <ThemeProvider theme={hasHoursForSelectedDay ? clockTheme : mainTheme}>
      <MobileTimePicker
        value={value ? moment(value) : null}
        minutesStep={5}
        label={label}
        onChange={handleTimeChange}
        slotProps={{
          actionBar: {
            actions: [],
          },
          layout: {
            sx: {
              '& .MuiClock-pin': {
                ...(hasHoursForSelectedDay && {
                  backgroundColor: isInReceivingHours ? Colors.green.light : Colors.red,
                }),
              },
            },
          },
          textField: {
            'data-testid': testId,
            placeholder: label,
            variant: inputVariant,
            fullWidth,
            size,
            InputLabelProps,
            error: (errorText && showFieldErrors) || showWarning,
            helperText: errorText,
          },
        }}
        {...rest}
      />
    </ThemeProvider>
  );
};

export default MinMaxTimePicker;
